import classNames from 'classnames'
import { CrudStoreItem } from 'legacy/lut/classes/models/CrudStoreItem'
import { DataModel } from 'legacy/lut/classes/models/DataModel'
import { RenderWhen } from 'legacy/lut/components/shared/renderWnen/RenderWhen'
import { useGetPartnerData } from 'legacy/lut/features/partner/hooks'
import { useUserRoles } from 'legacy/lut/hooks/useUserRoles'
import { mapContactFromContactAllData } from 'legacy/lut/services/contacts/mapping'
import { FC, JSX, useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { ContactEditor } from 'clickNgo/containers/contactEditor'
import { IContact } from 'clickNgo/entities/contact'
import { IContactFlags } from 'clickNgo/entities/contactFlag'
import { ContactCreator } from 'clickNgo/features/contact/contactCreator'
import { checkGlobalPartnerPage } from 'clickNgo/helpers/navigation'
import { useCheckReadOnlyAccess } from 'clickNgo/hooks/access'
import { Nullable } from 'clickNgo/models/utils'
import { IForm } from 'core-components/classes/models/IForm'
import { Table } from 'core-components/library/list'
import { IColumn, IRowAction, RowState, TableMode } from 'core-components/library/list/types'
import { LoaderOverlay } from 'core-components/library/loader'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'
import { Logic, Operator } from 'core-components/types'
import VisualAudience from 'core/components/shared/ui/VisualAudience'
import { CrudStore } from 'core/types/crudStore'

import { WidgetContactEditDialog } from '../../../contactEditDialog/ContactEditDialog'
import { ContactTileCell } from '../../../contactTileCell/ContactTileCell'
import { IEditContactParams } from '../../models'
import { ContactFilterArea } from '../contactFilterArea/ContactFilterArea'

import { AddNewContactButton } from './components/addNewContactButton/AddNewContactButton'
import { AddNewContactTile } from './components/addNewContactTile/AddNewContactTile'
import { getExportTooltip } from './components/contactAdditionalRow/helpers/getExportTooltip'
import { ContactDataCell } from './components/contactDataCell/ContactDataCell'
import { ContactDataRow } from './components/contactDataRow/ContactDataRow'
import { ContactListViewMode } from './components/contactListViewMode/ContactListViewMode'
import { convertIFormToDataModel } from './helpers/convertIFormToDataModel'
import { getContactFlags } from './helpers/getContactFlags'
import { getInitialContactData } from './helpers/getInitialContactData'
import { IContactListProps } from './models'

import styles from './ContactList.module.scss'

export const ContactList: FC<IContactListProps> = ({
  appSource,
  columns,
  flexWidth,
  ignoredFields,
  isEditActionDisabled,
  partnerId,
  source,
  onSelectProfile,
  onEditContact,
  metrics,
  hideAddNewButtons,
  hideFilterArea,
}) => {
  /** ---------------------- State / Variables ------------------------- */

  const { pathname } = useLocation()
  const isGlobalPartnerPage = checkGlobalPartnerPage(pathname)

  const [viewMode, setViewMode] = useState<TableMode>(TableMode.table)
  const [isContactDialogVisible, setContactDialogVisible] = useState(false)
  const [hasData, setHasData] = useState(false)
  const [contactFlags, setContactFlags] = useState<Nullable<IContactFlags>>(null)
  const [contact, setContact] = useState<Nullable<IContact>>(null)
  const [isTileButton, setIsTileButton] = useState(false)
  const [isNewContactDialogVisible, setIsNewContactDialogVisible] = useState(false)
  const [contactForEditing, setContactForEditing] = useState<DataModel | IForm>()
  const [preferredContacts, setPreferredContacts] = useState<IForm[] | DataModel[]>([])
  const [forceRerender, setForceRerender] = useState(-1)
  const [isLoading, setIsLoading] = useState(false)
  const { email, roleIds: roles } = useSelector(selectUser)
  const { partnerData: partner } = useGetPartnerData(isGlobalPartnerPage ? '' : partnerId)
  const { isSuperAdmin } = useUserRoles()

  const isReadOnlyRoleAccess = useCheckReadOnlyAccess()

  const isTable = useMemo(() => viewMode === TableMode.table, [viewMode])

  const tableClassName = useMemo(
    () => classNames(!isTable && hasData ? styles.tableFlex : '', 'cmp-contactDataNew-table'),
    [isTable, hasData]
  )

  const togglePreferredContact = useCallback(
    (item: IForm) => {
      const preferredContactIndex = preferredContacts.findIndex(
        (el) => el.ContactId === item.ContactID
      )
      const crud = CrudStore.get(CrudStoreItem.PreferredContacts, 'preferred-contact')
      setIsLoading(true)
      if (~preferredContactIndex) {
        crud.delete('638', preferredContacts[preferredContactIndex]).then(() => {
          setPreferredContacts(
            preferredContacts.filter((_el, index) => index !== preferredContactIndex)
          )
          setForceRerender(Math.random())
          setIsLoading(false)
        })
      } else {
        crud
          .create('638', [
            {
              ContactId: item.ContactID,
              PartnerId: item.PartnerID,
              TopPartnerId: item.TopPartnerId || item.TopPartnerID,
              UserEmail: email,
            },
          ])
          .then((result) => {
            setPreferredContacts([...preferredContacts, result[0]])
            setForceRerender(Math.random())
            setIsLoading(false)
          })
      }
    },
    [preferredContacts, email]
  )

  const rowActions = useMemo(() => {
    const newRowActions: IRowAction<DataModel | IForm>[] = []
    if (!isEditActionDisabled) {
      newRowActions.push({
        id: 'edit',
        action: (item) => {
          const selectedContact = convertIFormToDataModel([item])[0]
          setContactForEditing(selectedContact)
        },
        title: 'Edit',
        icon: 'kbase kbase-edit kbase-sm',
      })
    }

    if (!isEditActionDisabled && !isGlobalPartnerPage && isSuperAdmin) {
      newRowActions.push({
        id: 'edit v2',
        action: (item: IForm) => {
          setIsLoading(true)
          const selectedContact = mapContactFromContactAllData(item)
          setContact(selectedContact)
          getContactFlags(selectedContact)
            .then((data) => {
              setContactFlags(data)
              setContactDialogVisible(true)
            })
            .finally(() => {
              setIsLoading(false)
            })
        },
        title: 'Edit V2',
        icon: 'kbase kbase-edit kbase-sm',
      })
    }

    newRowActions.unshift({
      id: 'favorite',
      action: (item) => {
        togglePreferredContact(item)
      },
      title: 'Preferred',
      icon: (item: IForm) => {
        const isPreferred = preferredContacts.find(
          (el) => !el.DELETED && el.ContactId === item.ContactID
        )
        return isPreferred ? 'kbase kbase-star-alt' : 'kbase kbase-star'
      },
    })

    if (onSelectProfile) {
      newRowActions.unshift({
        id: 'Select',
        action: (item) => {
          const selectedContact = convertIFormToDataModel([item])[0]
          onSelectProfile(selectedContact)
        },
        title: 'Select',
        icon: 'kbase kbase-add',
      })
    }

    return newRowActions
  }, [
    isEditActionDisabled,
    isGlobalPartnerPage,
    isSuperAdmin,
    onSelectProfile,
    togglePreferredContact,
    preferredContacts,
  ])

  /** ---------------------- Handlers ------------------------- */

  const onRowTblHandler = useCallback(
    (
      item: DataModel | IForm,
      renderChildren: (
        contact: DataModel | IForm,
        rowState: RowState,
        additional: IObjectAny,
        focusedId?: IObjectAny,
        rowAttrs?: IObjectAny
      ) => JSX.Element,
      rowState: RowState,
      rowAttrs: IObjectAny,
      additional: IObjectAny
    ) => {
      return isTable ? (
        <ContactDataRow
          rowAttrs={rowAttrs}
          contact={item}
          renderChildren={renderChildren}
          rowState={rowState}
          additional={additional}
          colsNumber={columns.length}
        />
      ) : (
        <ContactTileCell item={convertIFormToDataModel([item])[0]} />
      )
    },
    [columns.length, isTable]
  )

  const onCellTplHandler = useMemo(() => {
    if (isTable) {
      return (contact: IForm, column: IColumn, cellAttrs: IObjectAny) => (
        <ContactDataCell
          contact={convertIFormToDataModel([contact])[0]}
          column={column}
          cellAttrs={cellAttrs}
        />
      )
    } else {
      return undefined
    }
  }, [isTable])

  const onCloseEditDialogHandler = useCallback(
    (params?: IEditContactParams) => {
      if (onEditContact) {
        onEditContact(params)
        source.removeQueries(true)
      }
      setContactForEditing(undefined)
    },
    [onEditContact, source, setContactForEditing]
  )

  const onCloseEditDialogHandlerV2 = useCallback(
    (params?: IEditContactParams) => {
      if (onEditContact) {
        onEditContact(params)
        source.removeQueries(true)
      }
      setContact(null)
      setContactFlags(null)
      setContactDialogVisible(false)
    },
    [onEditContact, source]
  )

  const onAddNewContactClickHandler = useCallback(async () => {
    const { initialData } = await getInitialContactData({ partnerId })

    setContactForEditing(initialData)
  }, [partnerId])

  // const onAddNewContactClickHandlerV2 = useCallback(async () => {
  //   setIsNewContactDialogVisible(true)
  // }, [])

  /** ---------------------- Effects ------------------------- */

  useEffect(() => {
    source.fetch().then(({ total }) => {
      setHasData(total > 0)
    })
  }, [source])

  useEffect(() => {
    const crud = CrudStore.get(CrudStoreItem.PreferredContacts, 'preferred-contact')
    crud.setFilter({
      logic: Logic.AND,
      filters: [
        {
          field: 'UserEmail',
          operator: Operator.EQUAL,
          value: email,
        },
      ],
    })

    crud.fetch({ force: true }).then((result) => {
      setPreferredContacts(result.items)
      setForceRerender(Math.random())
    })

    return crud.subscribe('change', (data) => {
      if (typeof data !== 'boolean') {
        setPreferredContacts(data)
        setForceRerender(Math.random())
      }
    })
  }, [email])

  useEffect(() => {
    setIsTileButton(!isTable && hasData)
  }, [hasData, isTable])

  return (
    <>
      <ContactListViewMode onChangeViewMode={setViewMode} />
      <RenderWhen condition={!hideFilterArea}>
        <ContactFilterArea source={source} ignoredFields={ignoredFields} />
      </RenderWhen>

      <div style={{ display: 'flex', gap: '16px' }}>
        <RenderWhen condition={!hideAddNewButtons}>
          {!isTileButton && (
            <AddNewContactButton onAddNewContactClick={onAddNewContactClickHandler} />
          )}
          {isTileButton && <AddNewContactTile onAddNewContactClick={onAddNewContactClickHandler} />}
        </RenderWhen>
      </div>

      <LoaderOverlay show={isLoading} />
      <Table
        key={forceRerender}
        columns={columns}
        source={source}
        className={tableClassName}
        flexWidth={flexWidth}
        measure={metrics}
        exportTooltip={getExportTooltip}
        hasExport
        mode={viewMode}
        header={isTable}
        pagination
        rowActions={isTable ? rowActions : []}
        rowTpl={onRowTblHandler}
        cellTpl={onCellTplHandler}
        rowActionsVisible="visible"
      />

      {contactForEditing && contactForEditing.get('ContactID') && (
        <VisualAudience
          read={['Read Only']}
          write={roles.filter((el: string) => el !== 'Read Only')}
          component={WidgetContactEditDialog}
          dialogEntryId={contactForEditing.get('ContactID')}
          entryIdItem={contactForEditing}
          appSource={appSource}
          onClose={onCloseEditDialogHandler}
        />
      )}

      {contact && contactFlags && partner && (
        <ContactEditor
          contact={contact}
          contactFlags={contactFlags}
          isVisible={isContactDialogVisible}
          onClose={onCloseEditDialogHandlerV2}
          onContactFlagsUpdate={() => {}}
          onContactUpdate={() => {}}
          isReadOnly={isReadOnlyRoleAccess}
          isNewContact={false}
        />
      )}

      {partner && (
        <ContactCreator
          partner={partner}
          isVisible={isNewContactDialogVisible}
          onReactivate={() => {
            setIsNewContactDialogVisible(false)
          }}
          onClose={() => {
            setIsNewContactDialogVisible(false)
          }}
          onCreate={async () => {}}
        />
      )}
    </>
  )
}
